import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  BaseQueryApi,
} from '@reduxjs/toolkit/query';
import type { RefreshTokenResponse } from "./../../../../../apps/hr-portal/src/features/auth/types";
import { REFRESH_TOKEN_CONFIG } from './refreshTokenConstants';
import { USER_ROUTES, HTTP_METHODS } from '../../constants';

/**
 * Singleton state management for token refresh
 * Prevents multiple simultaneous refresh attempts
 */
let isRefreshing = false;
let refreshPromise: Promise<{
  data: RefreshTokenResponse | undefined;
  error: FetchBaseQueryError | undefined;
}> | null = null;
let refreshRetryCount = 0;

/**
 * Attempts to refresh the access token
 * Uses singleton pattern to prevent concurrent refresh requests
 * Uses AbortSignal for proper timeout cancellation (RTK Query supported)
 * 
 * @param refreshBaseQuery - The base query function for making refresh requests
 * @param api - RTK Query API object with dispatch and other utilities
 * @returns Promise with refresh result (data or error)
 */
export async function attemptTokenRefresh(
  refreshBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
  api: BaseQueryApi
): Promise<{
  data: RefreshTokenResponse | undefined;
  error: FetchBaseQueryError | undefined;
}> {
  // If already refreshing, return the existing promise and prevents multiple refresh calls
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  // Check retry limit to prevent infinite refresh loops
  if (refreshRetryCount >= REFRESH_TOKEN_CONFIG.MAX_RETRIES) {
    return {
      data: undefined,
      error: {
        status: 'CUSTOM_ERROR',
        error: 'Maximum refresh retries exceeded',
      } as FetchBaseQueryError,
    };
  }

  // Start new refresh attempt
  isRefreshing = true;
  refreshRetryCount += 1;

  refreshPromise = (async () => {
    // AbortController cancels the request if it exceeds timeout
    const abortController = new AbortController();
    let timeoutId: NodeJS.Timeout | null = null;
    let wasTimeoutAbort = false;

    try {
      // Set up timeout that aborts the request
      timeoutId = setTimeout(() => {
        wasTimeoutAbort = true;
        abortController.abort(); // Actually cancels the fetch request
      }, REFRESH_TOKEN_CONFIG.TIMEOUT_MS);

      // Call refresh with AbortSignal
      // RTK Query supports signal in FetchArgs
      const refreshResult = await refreshBaseQuery(
        {
          url: USER_ROUTES.refreshToken,
          method: HTTP_METHODS.POST,
          headers: {
            accept: '*/*',
          },
          signal: abortController.signal,
        },
        api,
        {}
      );

      // Clear timeout if request completed before timeout
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }

      // Reset retry count on successful refresh
      if (refreshResult.data && (refreshResult.data as RefreshTokenResponse).success) {
        refreshRetryCount = 0;
      }

      return refreshResult as {
        data: RefreshTokenResponse | undefined;
        error: FetchBaseQueryError | undefined;
      };
    } catch (error: unknown) {
      // Clear timeout if error occurred
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }

      // Check if error was due to timeout (AbortError)
      if (error && typeof error === 'object' && 'name' in error && error.name === 'AbortError') {
        return {
          data: undefined,
          error: {
            status: wasTimeoutAbort ? 'TIMEOUT_ERROR' : 'CANCELLED',
            error: wasTimeoutAbort
              ? 'Refresh token request timed out'
              : 'Refresh token request was cancelled',
          } as FetchBaseQueryError,
        };
      }

      // Return other errors
      return {
        data: undefined,
        error: error as FetchBaseQueryError,
      };
    } finally {
      // Always reset the refreshing flag
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}